import { Add, ChatBubbleOutline, Close, DescriptionOutlined, History, OpenInFull, SmartToyOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { isAxiosError } from 'axios'
import type React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import useSWR from 'swr'

import ChatflowsApi from '@api/agentBuilder/chatflows'
import { useAgentApi } from '@api/hooks'
import { ChatFileResponse, ChatFileTypeParam, SessionResponse } from '@api/generated/internal/internalApi'
import { PermissionErrorDetails } from '@api/generated/public/publicApi'
import Chat from '@components/Chat'
import PromptBox, { PromptBoxForm, PromptBoxRef } from '@components/PromptBox'
import { InferenceProvider, useInference } from '@/contexts/inference'
import useApiClient from '@hooks/useApiClient'
import { SYSTEM_TEAMMATE_IDS } from '@/constants/systemTeammateIds'
import { selectDefaultSessionId } from '@/utils/sessions'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useAuth } from 'torqcloud-ui'

type AssistantTab = 'chat' | 'history' | 'files'

interface AssistantChatProps {
  open: boolean
  onClose: () => void
}

const DEFAULT_WIDTH = 460
const MIN_WIDTH = 300
const MAX_WIDTH_RATIO = 0.5
const ASSISTANT_TEAMMATE_KEY = 'global-assistant-workforce-teammate'
const ASSISTANT_SESSIONS_KEY = 'global-assistant-sessions'
const SESSION_FILES_KEY = 'global-assistant-session-files'

type MissingResources = PermissionErrorDetails['missingResources']
type AssistantTeammate = {
  id: string
  name: string
}

export default function AssistantChat({ open, onClose }: AssistantChatProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [activeTab, setActiveTab] = useState<AssistantTab>('chat')
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [selectedSessionId, setSelectedSessionId] = useState<string>()
  const [messageResetKey, setMessageResetKey] = useState(0)
  const creatingSessionRef = useRef(false)
  const resetAssistantMessages = useCallback(() => {
    setMessageResetKey(key => key + 1)
  }, [])
  const api = useAgentApi()
  const apiClientContext = useApiClient()
  const { user } = useAuth()
  const { currentOrganizationId } = useOrganization()
  const chatflowsApi = useMemo(() => new ChatflowsApi(apiClientContext.torqAgentClient), [apiClientContext.torqAgentClient])

  // The built-in Workforce Assistant (WF-2616), the same row in every organization. This used to
  // pick whichever Teammate the user had touched most recently, which was a placeholder: the header
  // has always fallen back to the name "Workforce Assistant". A listing query could not find it now
  // in any case, because built-in rows are excluded from every list by design; it has to be fetched
  // by its derived id.
  const { data: teammateResponse, isLoading: teammateLoading } = useSWR(
    [ASSISTANT_TEAMMATE_KEY, currentOrganizationId],
    async () => {
      const response = await chatflowsApi.getSpecificChatflow(SYSTEM_TEAMMATE_IDS['workforce-assistant'])
      return response.data as AssistantTeammate | undefined
    },
    {
      // An API that predates the built-in Teammates returns 404 here. That is a missing feature, not
      // a transient fault, so do not retry it on a loop behind the panel. Everything else still
      // retries: blanket `false` also swallowed 5xx and network blips, which pinned the panel at
      // "Workforce Assistant unavailable" until the user reloaded.
      shouldRetryOnError: (error: unknown) => !(isAxiosError(error) && error.response?.status === 404),
    }
  )
  const teammate = teammateResponse
  const chatflowId = teammate?.id

  const {
    data: sessionsResponse,
    mutate: refreshSessionList,
    isLoading: sessionsLoading,
  } = useSWR(open && chatflowId ? [ASSISTANT_SESSIONS_KEY, currentOrganizationId, chatflowId] : null, async () => {
    if (!chatflowId) return []
    const response = await api.sessions.listSessions({ chatflowId })
    return response.data ?? []
  })
  const sessions = useMemo(() => sessionsResponse ?? [], [sessionsResponse])
  const defaultSessionId = useMemo(() => selectDefaultSessionId(sessions, user?.id), [sessions, user?.id])
  const currentSession = sessions.find(session => session.id === selectedSessionId)

  const { data: fileAcceptTypes } = useSWR(open && chatflowId ? `global-assistant-accepted-files:${chatflowId}` : null, async () => {
    if (!chatflowId) return
    const response = await api.chatFiles.getAcceptedFileTypesByChatflow(chatflowId)
    return response.data
  })

  const { data: sessionFilesResponse, mutate: refreshSessionFiles } = useSWR(
    open && chatflowId && selectedSessionId ? [SESSION_FILES_KEY, chatflowId, selectedSessionId] : null,
    async () => {
      if (!chatflowId || !selectedSessionId) return []
      const response = await api.chatFiles.listChatFiles(chatflowId, {
        sessionId: selectedSessionId,
        types: [ChatFileTypeParam.Generated],
      })
      return (response.data as ChatFileResponse[] | undefined) ?? []
    }
  )
  const sessionFiles = sessionFilesResponse ?? []

  useEffect(() => {
    resetAssistantMessages()
    setSelectedSessionId(undefined)
  }, [chatflowId, resetAssistantMessages])

  useEffect(() => {
    if (!chatflowId || !sessionsResponse) return
    if (creatingSessionRef.current) return
    if (selectedSessionId && sessions.some(session => session.id === selectedSessionId)) return
    if (defaultSessionId) {
      resetAssistantMessages()
      setSelectedSessionId(defaultSessionId)
    }
  }, [chatflowId, defaultSessionId, resetAssistantMessages, selectedSessionId, sessions, sessionsResponse])

  useEffect(() => {
    if (!chatflowId || sessionsResponse === undefined || sessionsResponse.length > 0 || selectedSessionId || creatingSessionRef.current)
      return

    creatingSessionRef.current = true
    void api.sessions
      .createSession({ chatflowId, title: 'New Session' })
      .then(async response => {
        if (response.error || !response.data) {
          toast.error('Failed to create assistant session')
          return
        }
        setSelectedSessionId(response.data.id)
        await refreshSessionList()
      })
      .finally(() => {
        creatingSessionRef.current = false
      })
  }, [api, chatflowId, refreshSessionList, selectedSessionId, sessionsResponse])

  const handleNewSession = useCallback(async () => {
    if (!chatflowId) return
    creatingSessionRef.current = true
    try {
      const response = await api.sessions.createSession({ chatflowId, title: 'New Session' })
      if (response.error || !response.data) {
        toast.error('Failed to create assistant session')
        return
      }
      resetAssistantMessages()
      setSelectedSessionId(response.data.id)
      setActiveTab('chat')
      await refreshSessionList()
    } finally {
      creatingSessionRef.current = false
    }
  }, [api, chatflowId, refreshSessionList, resetAssistantMessages])

  const startResize = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      const startX = event.clientX
      const startWidth = width
      const maxWidth = Math.max(MIN_WIDTH, Math.floor(window.innerWidth * MAX_WIDTH_RATIO))

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const nextWidth = startWidth + startX - moveEvent.clientX
        setWidth(Math.min(maxWidth, Math.max(MIN_WIDTH, nextWidth)))
      }

      const handleMouseUp = () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }

      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    },
    [width]
  )

  const loading = teammateLoading || sessionsLoading || (!!chatflowId && !selectedSessionId && sessionsResponse === undefined)
  const shouldRenderContent = open

  const panel = (
    <InferenceProvider key={`${chatflowId ?? 'no-teammate'}:${selectedSessionId ?? 'no-session'}`} resetKey={messageResetKey}>
      <Box
        data-component="global-assistant-sidebar"
        sx={{
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: t => t.colors.base[70],
          color: t => t.palette.text.primary,
        }}
      >
        <Box
          data-component="global-assistant-sidebar__header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1.25,
            backgroundColor: t => t.colors.base[60],
            borderBottom: t => `1px solid ${t.colors.base[50]}`,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              display: 'grid',
              placeItems: 'center',
              backgroundColor: t => t.palette.primary.main,
              color: t => t.colors.base[40],
              flexShrink: 0,
            }}
          >
            <SmartToyOutlined sx={{ fontSize: 21 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.25 }} noWrap>
              {teammate?.name ?? 'Workforce Assistant'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.8125rem', fontStyle: 'normal', fontWeight: 400 }} noWrap>
              {selectedSessionId ? currentSession?.title ?? 'Assistant session' : 'Ready'}
            </Typography>
          </Box>
          <Tooltip title="New session">
            <span>
              <IconButton aria-label="New assistant session" size="small" onClick={handleNewSession} disabled={!chatflowId}>
                <Add fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close assistant">
            <IconButton aria-label="Close assistant" size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          data-component="global-assistant-sidebar__content"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {shouldRenderContent && loading && <AssistantCenteredMessage icon={<CircularProgress size={18} />} title="Loading assistant" />}
          {shouldRenderContent && !loading && !teammate && <AssistantCenteredMessage title="Workforce Assistant unavailable" />}
          {shouldRenderContent && !loading && teammate && activeTab === 'chat' && (
            <AssistantChatTab
              key={`${chatflowId}:${selectedSessionId}`}
              teammate={teammate}
              session={currentSession}
              sessionId={selectedSessionId}
              acceptedFileTypes={fileAcceptTypes?.allSupportedTypes}
              onRefreshSessions={refreshSessionList}
              onRefreshFiles={refreshSessionFiles}
              onAuthzError={missingResources => {
                toast.error(missingResources ? 'Assistant permissions are missing required resources.' : 'Assistant access denied')
              }}
            />
          )}
          {shouldRenderContent && !loading && teammate && activeTab === 'history' && (
            <AssistantHistoryTab
              sessions={sessions}
              currentSessionId={selectedSessionId}
              onSelectSession={sessionId => {
                resetAssistantMessages()
                setSelectedSessionId(sessionId)
                setActiveTab('chat')
              }}
            />
          )}
          {shouldRenderContent && !loading && teammate && activeTab === 'files' && <AssistantFilesTab files={sessionFiles} />}
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, value: AssistantTab) => setActiveTab(value)}
          variant="fullWidth"
          aria-label="Assistant sections"
          sx={{
            minHeight: 44,
            backgroundColor: t => t.colors.base[60],
            borderTop: t => `1px solid ${t.colors.base[50]}`,
            flexShrink: 0,
            '& .MuiTab-root': {
              minHeight: 44,
              py: 0.5,
              px: 0.5,
              fontSize: '0.6875rem',
              fontWeight: 500,
              textTransform: 'none',
              color: t => t.colors.base[45],
              '&.Mui-selected': {
                color: t => t.palette.primary.main,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: t => t.palette.primary.main,
            },
          }}
        >
          <Tab value="chat" label="Chat" icon={<ChatBubbleOutline sx={{ fontSize: 18 }} />} />
          <Tab value="history" label="History" icon={<History sx={{ fontSize: 18 }} />} />
          <Tab value="files" label="Files" icon={<DescriptionOutlined sx={{ fontSize: 18 }} />} />
        </Tabs>
      </Box>
    </InferenceProvider>
  )

  if (isMobile) {
    return (
      <Dialog fullScreen open={open} onClose={onClose} keepMounted PaperProps={{ sx: { backgroundColor: t => t.colors.base[70] } }}>
        <DialogContent sx={{ p: 0, display: 'flex', minHeight: 0 }}>{panel}</DialogContent>
      </Dialog>
    )
  }

  return (
    <Box
      component="aside"
      data-component="global-assistant-sidebar-shell"
      aria-hidden={!open}
      sx={{
        position: 'relative',
        width: open ? width : 0,
        minWidth: open ? MIN_WIDTH : 0,
        maxWidth: `${MAX_WIDTH_RATIO * 100}vw`,
        flexShrink: 0,
        height: '100%',
        py: open ? 1 : 0,
        pr: open ? 1 : 0,
        pl: 0,
        display: 'flex',
        overflow: 'hidden',
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <Box
        onMouseDown={startResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize assistant sidebar"
        sx={{
          display: open ? 'block' : 'none',
          position: 'absolute',
          left: -3,
          top: 12,
          bottom: 12,
          width: 8,
          cursor: 'col-resize',
          zIndex: 2,
          '&:hover::after': {
            backgroundColor: t => t.palette.primary.main,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 3,
            top: 0,
            bottom: 0,
            width: 2,
            borderRadius: 1,
            backgroundColor: 'transparent',
          },
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          borderRadius: '8px',
          border: t => `1px solid ${t.colors.base[50]}`,
          boxShadow: t => t.shadows[6],
        }}
      >
        {panel}
      </Box>
    </Box>
  )
}

function AssistantChatTab({
  teammate,
  session,
  sessionId,
  acceptedFileTypes,
  onRefreshSessions,
  onRefreshFiles,
  onAuthzError,
}: {
  teammate: AssistantTeammate
  session?: SessionResponse
  sessionId?: string
  acceptedFileTypes?: string[]
  onRefreshSessions: () => Promise<SessionResponse[] | undefined>
  onRefreshFiles: () => Promise<ChatFileResponse[] | undefined>
  onAuthzError: (missingResources?: MissingResources) => void
}) {
  if (!sessionId) {
    return <AssistantCenteredMessage title="No active session" />
  }

  return (
    <AssistantLiveChat
      teammate={teammate}
      session={session}
      sessionId={sessionId}
      acceptedFileTypes={acceptedFileTypes}
      onRefreshSessions={onRefreshSessions}
      onRefreshFiles={onRefreshFiles}
      onAuthzError={onAuthzError}
    />
  )
}

function AssistantLiveChat({
  teammate,
  session,
  sessionId,
  acceptedFileTypes,
  onRefreshSessions,
  onRefreshFiles,
  onAuthzError,
}: {
  teammate: AssistantTeammate
  session?: SessionResponse
  sessionId: string
  acceptedFileTypes?: string[]
  onRefreshSessions: () => Promise<SessionResponse[] | undefined>
  onRefreshFiles: () => Promise<ChatFileResponse[] | undefined>
  onAuthzError: (missingResources?: MissingResources) => void
}) {
  const { user, permissions } = useAuth()
  const promptBoxRef = useRef<PromptBoxRef>(null)
  const isViewingOthersSession = !!session?.userId && !!user && session.userId !== user.id
  const {
    handleSubmitMessage,
    loading,
    messages,
    approveToolCall,
    rejectToolCall,
    cancelSubTeammate,
    acceptSubTeammate,
    submitSubTeammateFollowUp,
    submitMessageFeedback,
  } = useInference(teammate.id, onAuthzError, sessionId, {
    resetMessagesOnUnmount: false,
  })

  const blockedReason = isViewingOthersSession ? 'Read-only: this session belongs to another user.' : undefined

  const handleSubmit = useCallback(
    async (values: PromptBoxForm) => {
      promptBoxRef.current?.clearFiles()
      promptBoxRef.current?.setPrompt('')
      await handleSubmitMessage(values)
      await onRefreshSessions()
      await onRefreshFiles()
    },
    [handleSubmitMessage, onRefreshFiles, onRefreshSessions]
  )

  return (
    <Box
      data-component="global-assistant-sidebar__live-chat"
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, px: 1, py: 1, overflow: 'hidden' }}>
        <Chat
          chatflowId={teammate.id}
          user={user}
          messages={messages}
          showLoadingMessage={loading}
          canSubmitMessageFeedback={!isViewingOthersSession}
          onSubmitMessageFeedback={submitMessageFeedback}
          onApproveToolCall={approveToolCall}
          onRejectToolCall={rejectToolCall}
          onCancelSubTeammate={cancelSubTeammate}
          onAcceptSubTeammate={acceptSubTeammate}
          onSubmitSubTeammateFollowUp={submitSubTeammateFollowUp}
          collapseMarkdown={false}
        />
      </Box>
      <Box sx={{ flexShrink: 0, p: 1, backgroundColor: t => t.colors.base[70], borderTop: t => `1px solid ${t.colors.base[50]}` }}>
        <PromptBox
          ref={promptBoxRef}
          variant="small"
          placeholder={`Message ${teammate.name}`}
          color="dark-grey"
          submitDisabled={isViewingOthersSession || (!permissions.canQueueMessages && loading)}
          inputDisabled={isViewingOthersSession}
          blockedReason={blockedReason}
          onSubmit={handleSubmit}
          allowUpload
          deferFileUpload
          allowedFileTypes={acceptedFileTypes}
        />
      </Box>
    </Box>
  )
}

function AssistantHistoryTab({
  sessions,
  currentSessionId,
  onSelectSession,
}: {
  sessions: SessionResponse[]
  currentSessionId?: string
  onSelectSession: (sessionId: string) => void
}) {
  if (sessions.length === 0) {
    return <AssistantCenteredMessage title="No sessions yet" />
  }

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75, overflowY: 'auto' }}>
      {sessions.map(session => {
        const isCurrent = session.id === currentSessionId
        const date = new Date(session.latestMessageDate ?? session.createdDate)
        const isToday = date.toDateString() === new Date().toDateString()
        return (
          <Button
            key={session.id}
            type="button"
            onClick={() => onSelectSession(session.id)}
            sx={{
              justifyContent: 'flex-start',
              gap: 0.75,
              p: 1,
              borderRadius: '8px',
              textAlign: 'left',
              color: 'text.primary',
              backgroundColor: t => (isCurrent ? t.colors.base[50] : t.colors.base[60]),
              border: t => `1px solid ${isCurrent ? t.palette.primary.main : t.colors.base[50]}`,
              '&:hover': {
                backgroundColor: t => t.colors.base[50],
              },
            }}
          >
            <History sx={{ fontSize: 18, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
                {session.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'normal', fontWeight: 400 }} noWrap>
                {isToday ? date.toLocaleTimeString() : date.toLocaleDateString()}
              </Typography>
            </Box>
          </Button>
        )
      })}
    </Box>
  )
}

function AssistantFilesTab({ files }: { files: ChatFileResponse[] }) {
  if (files.length === 0) {
    return <AssistantCenteredMessage title="No generated files" />
  }

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75, overflowY: 'auto' }}>
      {files.map(file => (
        <Box
          key={file.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            p: 1,
            borderRadius: '8px',
            backgroundColor: t => t.colors.base[60],
            border: t => `1px solid ${t.colors.base[50]}`,
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 18, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {file.url ? (
              <Link href={file.url} target="_blank" rel="noopener noreferrer" color="primary" underline="hover" sx={{ display: 'block' }}>
                <Typography variant="body1" sx={{ fontWeight: 400 }} noWrap>
                  {file.fileName}
                </Typography>
              </Link>
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 400 }} noWrap>
                {file.fileName}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'normal', fontWeight: 400 }} noWrap>
              {file.contentType}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

function AssistantCenteredMessage({ title, icon }: { title: string; icon?: React.ReactNode }) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        placeItems: 'center',
        p: 2,
        color: 'text.secondary',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="body1" sx={{ fontWeight: 400 }}>
          {title}
        </Typography>
      </Box>
    </Box>
  )
}

export function GlobalAssistantOpenButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip title="Open Workforce Assistant">
      <IconButton aria-label="Open Workforce Assistant" color="primary" onClick={onClick}>
        <OpenInFull fontSize="small" />
      </IconButton>
    </Tooltip>
  )
}
